import { useState } from 'react';
import ReactJson from 'react-json-view';
import { useParams } from 'react-router-dom';
import _omit from 'lodash/omit';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { emptyObject } from '@cbhq/cds-utils';
import { Select, SelectOption } from '@cbhq/cds-web/controls';
import { VStack } from '@cbhq/cds-web/layout';
import { Alert } from '@cbhq/cds-web/overlays';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { useGetResource } from ':cloud/queries/ResourceQueries/useGetResource';
import { useUpgradeLimits } from ':cloud/queries/ResourceQueries/useUpgradeLimits';
import { useGetAvailableVersions } from ':cloud/queries/WorkflowQueries/useGetAvailableVersions';
import { getClusterStatus } from ':cloud/utils/clusters';
import { AppRoute } from ':cloud/utils/routes';
import { ClusterStatusTitle } from ':cloud/widgets/clusters/ClusterStatusTitle';
import { BackButtonTo, InlineCallout, LoadingSpinner } from ':cloud/widgets/sharedcomponents';
import { AuthField } from ':cloud/widgets/sharedcomponents/internal/AuthField';

type ParamOptions = {
  resourceId: string;
};

export function AdminClusterPortal() {
  const { resourceId } = useParams<ParamOptions>();
  const [visible, { toggleOn, toggleOff }] = useToggler();
  const { resource, isLoading: isLoadingResource } = useGetResource(resourceId);
  const { versions, isLoading: isLoadingVersions } = useGetAvailableVersions(
    resource?.type as string,
  );
  const upgradeLimits = useUpgradeLimits(resourceId);
  const [version, setVersion] = useState<string | undefined>();

  const triggerVersionChange = (value: string): void => {
    toggleOn();
    setVersion(value);
  };

  if (isLoadingResource || isLoadingVersions) {
    return <LoadingSpinner />;
  }

  if (!resource) {
    return <div>No resource found</div>;
  }

  const onHandleUpgradeResource = (): void => {
    if (version) {
      upgradeLimits.mutate(version, {
        onSuccess: () => {
          // eslint-disable-next-line no-console
          console.log(`Sucessfully updated version to ${version}`);
          setVersion(version);
        },
      });
    }
    toggleOff();
  };

  const { internalStatus, status } = getClusterStatus(resource.state); // Prioritize InternalStatus

  return (
    <VStack spacingVertical={5} spacingHorizontal={8} gap={4}>
      {/* back button */}
      <BackButtonTo to={AppRoute.Admin.Clusters} label="All clusters" />

      {/* cluster status */}
      <ClusterStatusTitle withIcon cluster={resource} />

      {/* cluster metrics */}
      <VStack gap={2} maxWidth={600}>
        <div>
          <TextBody as="p">Internal Status:</TextBody>
          <TextHeadline as="h2" color="foreground">
            {internalStatus || status}
          </TextHeadline>
        </div>
        <InlineCallout type="info">
          If the cluster has gone through an upgrade, an internal status will presents you with a
          more detailed state than reported to the customer.
        </InlineCallout>
      </VStack>

      {/* switch workflow version */}
      <VStack spacingBottom={4} gap={2} width={300}>
        <TextHeadline as="h2">Workflow Version</TextHeadline>
        <Select value={resource.version} onChange={(val: string) => triggerVersionChange(val)}>
          {versions.map((option) => (
            <SelectOption value={option} key={option} title={option} />
          ))}
        </Select>
      </VStack>
      {resource.artifacts?.endpoint && (
        <AuthField code={resource.artifacts?.endpoint} label="Endpoint" />
      )}

      {/* artifacts */}
      <VStack width="50%" borderRadius="rounded" background="primaryWash" gap={4} spacing={4}>
        <TextHeadline as="h2">Artifacts</TextHeadline>
        <ReactJson src={resource.artifacts || emptyObject} />
      </VStack>

      {/* configuration */}
      <VStack width="50%" borderRadius="rounded" background="primaryWash" gap={4} spacing={4}>
        <TextHeadline as="h2">Configuration</TextHeadline>
        <ReactJson src={resource.configuration || emptyObject} />
      </VStack>

      {/* resources */}
      <VStack borderRadius="rounded" background="primaryWash" gap={4} spacing={4}>
        <TextHeadline as="h2">Resource</TextHeadline>
        <ReactJson src={_omit(resource, ['artifacts', 'configuration'])} />
      </VStack>

      <Alert
        visible={visible}
        title="Upgrade Resource"
        body={`This is going to update ${resource?.name} (${resource?.type}) to version ${version}. It might be irreversible. Confirm to upgrade.`}
        preferredActionLabel="Confirm"
        onPreferredActionPress={onHandleUpgradeResource}
        dismissActionLabel="Cancel"
        onDismissActionPress={toggleOff}
        onRequestClose={toggleOff}
      />
    </VStack>
  );
}
